import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import cn from "classnames";

import { rhytPatterns, solfaNameOptions, ABCOptions, intervalOptions } from "../../../common/parameters";
import scrollTo from "../../../common/helpers/scrollTo";
import Seo from "../../../common/helpers/seo";

import { IPlan } from "../../../common/types";
import { InputText, Select } from "../../components/Form";
import PlanCard from "../../components/PlanCard";
import Modal from "../../components/Modal";
import AdSense from "../../components/AdSense";
import { ISelectEvent } from "../../types";

import Opus from "./Opus";
import Rhythm from "./Rhythm";
import Pitch from "./Pitch";

import {
    StyledBrowse,
    BrowseContent,
    StyledFilters,
    PageNumbers,
    PageNumber,
    Holder,
    QueryBar,
    Hr,
    DeleteQuery,
    NoResult,
    TotalDocs,
    DesktopFilters,
    FilterButton,
    SortContainer,
    SortContainerMobile,
    SortLabel,
    SortWrapper,
    SortForDesktop,
    SortForMobile,
    AboveQueryAdSense,
    FilterBottomAdSense,
    BottomAdSense,
    TopMobileAdSense,
    AdWrapper,
} from "./styles";
import messages from "./messages";

import { IChangeData, IPageNumbersStyleProps, IProps, IState } from "./interfaces";
import { IBrowseState, IFilter } from "../../store/browse/models";

const isServer = !process.env.WEBPACK;
const isAdsEnabled = !isServer && window.isAdsEnabled === 1;

const getPlan = (plans: IPlan[], adsPlace: number, num: number) => {
    if (!isAdsEnabled) {
        return plans[num];
    }
    let firstAd, lastAd;
    if (window.innerWidth <= 600) {
        firstAd = 4;
        lastAd = 8;
    } else {
        const totalRows = window.innerWidth < 1200 ? 2 : 3;
        if (adsPlace === 0) {
            firstAd = 4;
            lastAd = totalRows === 3 ? 8 : 7;
        } else {
            firstAd = 5;
            lastAd = totalRows === 3 ? 7 : 6;
        }
    }
    if (num < firstAd) {
        return plans[num];
    } else if (num > lastAd) {
        return plans[num - 2];
    } else if (num === firstAd || num === lastAd) {
        if (num > plans.length) return null;
        return num === firstAd ? "ad1" : "ad2";
    } else {
        return plans[num - 1];
    }
};

class Browse extends Component<IProps, IState> {
    searchTimer?: ReturnType<typeof setTimeout>
    state: IState = {
        isFiltersModalOpen: false,
        pages: [-4, -3, -2, -1, 0, 1, 2, 3, 4],
        adsPlace: 0,
    };
    componentDidMount() {
        if (this.props.browse.timeTaken === 0) {
            this.getResults(false);
        }
        this.setPages();
        window.addEventListener("resize", this.setPages);
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.setPages);
    }

    setPages = () => {
        if (window.innerWidth < 550) {
            this.setState({ pages: [-1, 0, 1] });
        } else {
            this.setState({ pages: [-4, -3, -2, -1, 0, 1, 2, 3, 4] });
        }
    };
    getResults = (isScrollToTop: boolean) => {
        this.props.getResults();
        if (isScrollToTop) {
            scrollTo();
        }
        this.setState({ adsPlace: Math.floor(Math.random() * 2) });
    };
    change = (e: IChangeData) => {
        const { intl: { locale }, browse: { filters }, user: { solfaKam = false } } = this.props;
        if (e.target.name === "opus.planType") {
            let removeProps = ["folksongClass", "composer", "genre", "exerciseType", "folksongTempo", "era"];
            removeProps.forEach(prop => {
                let index = filters.findIndex(filter => filter.name === "opus." + prop);
                if (index !== -1) {
                    filters.splice(index, 1);
                }
            });
        }
        if (e.target.name === "rhythm.changingTS") {
            let removeProps = ["timeSignatureDen2", "timeSignatureNum2"];
            removeProps.forEach(prop => {
                let index = filters.findIndex(filter => filter.name === "rhythm." + prop);
                if (index !== -1) {
                    filters.splice(index, 1);
                }
            });
        }
        if (e.target.type === "checkbox") {
            let i = filters.findIndex(filter => filter.name === e.target.name);
            if (i !== -1) {
                if (e.target.checked === undefined) {
                    filters.splice(i, 1);
                } else {
                    filters[i] = {
                        name: e.target.name!,
                        value: e.target.checked!,
                    };
                }
            } else {
                filters.push({
                    name: e.target.name!,
                    value: e.target.checked!,
                });
            }
        } else {
            let i = filters.findIndex(filter => filter.name === e.target.name);
            let value = e.target.value;
            if (
                e.target.name === "rhythm.patterns" ||
                e.target.name === "pitch.solfaNames" ||
                e.target.name === "pitch.ABCnames" ||
                e.target.name === "pitch.intervals"
            ) {
                if (i !== -1) {
                    if (value === undefined) {
                        filters.splice(i, 1);
                    } else {
                        let valueI = (filters[i].value as any[]).indexOf(value);
                        if (valueI === -1) {
                            (filters[i].value as any[]).push(value);
                        } else {
                            (filters[i].value as any[]).splice(valueI, 1);
                            if ((filters[i].value as any[]).length === 0) {
                                filters.splice(i, 1);
                            }
                        }
                        if (filters[i] && filters[i].value) {
                            if (e.target.name === "rhythm.patterns") {
                                let rhythmsInOrder: string[] = [];
                                Object.keys(rhytPatterns(this.props.intl.formatMessage)).forEach(r => {
                                    if ((filters[i].value as any[]).indexOf(r) !== -1) {
                                        rhythmsInOrder.push(r);
                                    }
                                });
                                filters[i].value = rhythmsInOrder;
                            } else if (e.target.name === "pitch.solfaNames") {
                                let solfaNamesInOrder: string[] = [];
                                solfaNameOptions(locale, solfaKam).forEach(s => {
                                    if ((filters[i].value as any[]).indexOf(s.value) !== -1) {
                                        solfaNamesInOrder.push(s.value);
                                    }
                                });
                                filters[i].value = solfaNamesInOrder;
                            } else if (e.target.name === "pitch.ABCnames") {
                                let ABCInOrder: string[] = [];
                                ABCOptions(locale).forEach(a => {
                                    if ((filters[i].value as any[]).indexOf(a.value) !== -1) {
                                        ABCInOrder.push(a.value);
                                    }
                                });
                                filters[i].value = ABCInOrder;
                            } else if (e.target.name === "pitch.intervals") {
                                let intervalsInOrder: string[] = [];
                                intervalOptions(locale).forEach(a => {
                                    if ((filters[i].value as any[]).indexOf(a.value) !== -1) {
                                        intervalsInOrder.push(a.value);
                                    }
                                });
                                filters[i].value = intervalsInOrder;
                            }
                        }
                    }
                } else {
                    filters.push({
                        name: e.target.name,
                        value: [value],
                    });
                }
            } else {
                if (e.target.value === undefined || e.target.value === "") {
                    if (i !== -1) {
                        filters.splice(i, 1);
                    }
                } else {
                    if (e.target.type === "number") {
                        value = +value;
                    }
                    if (i !== -1) {
                        filters[i] = {
                            name: e.target.name!,
                            value,
                        };
                    } else {
                        filters.push({
                            name: e.target.name!,
                            value,
                        });
                    }
                }
            }
        }
        this.props.setFilters(filters);
        this.getResults(false);
    };
    changeSearch = (e: React.FormEvent<HTMLInputElement>) => {
        const search = e.currentTarget.value;
        this.props.setSearch(search);
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
            this.getResults(false);
        }, 1000);
    };
    removeFilters = () => {
        this.props.clearFilters();
        this.getResults(true);
    };
    goToPage = (page: number) => {
        this.props.setPage(page);
        this.getResults(true);
    };
    handleChangeSort = (e: ISelectEvent<IBrowseState["sorting"]>) => {
        this.props.changeSort(e.target.value);
        this.getResults(false);
    }
    renderFilters = () => {
        const { filters, sorting } = this.props.browse;
        const {
            intl: { formatMessage },
        } = this.props;
        return (
            <StyledFilters>
                <h2
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <FormattedMessage {...messages.filters} />
                    {filters.length > 0 && (
                        <DeleteQuery onClick={this.removeFilters}>
                            <FormattedMessage {...messages.deleteFilters} />
                        </DeleteQuery>
                    )}
                </h2>
                <SortForMobile>
                    <SortContainerMobile>
                        <SortLabel>
                            <FormattedMessage {...messages.sortType} />:{" "}
                        </SortLabel>
                        <SortWrapper>
                            <Select
                                name="sortType"
                                noDelete
                                value={sorting}
                                onChange={this.handleChangeSort}
                                options={[
                                    { label: formatMessage(messages.popularity), value: "total" },
                                    { label: formatMessage(messages.newsFirst), value: "time" },
                                ]}
                            />
                        </SortWrapper>
                    </SortContainerMobile>
                </SortForMobile>
                <Opus filters={filters} change={this.change} />
                <Rhythm filters={filters} change={this.change} />
                <Pitch filters={filters} change={this.change} />
                {/* {isAdsEnabled && <AdSense.Google
                    style={{height: 340}}
                    format=''
                    client={adsenseClientId}
                    slot={adSlots.filterBottom}
                />} */}
            </StyledFilters>
        );
    };

    handleComposerClick = (composer: string) => {
        this.props.clearFilters();
        const filters: IFilter[] = [];
        filters.push({
            name: "opus.planType",
            value: "written",
        }, {
            name: "opus.composer",
            value: composer,
        })
        this.props.setFilters(filters);
        this.getResults(true);
    }
    render() {
        const {
            intl: { formatMessage },
            browse: { plans, search, page, totalPages, limit, totalDocs, timeTaken, sorting, loading },
        } = this.props;
        const {
            isFiltersModalOpen,
            pages,
            adsPlace,
        } = this.state;

        const Pagination = ({ down }: IPageNumbersStyleProps) => (
            <>
                {totalDocs > limit && (
                    <PageNumbers down={down}>
                        {pages.map(p => {
                            if (p + page >= 1 && p + page <= totalPages) {
                                return (
                                    <PageNumber
                                        key={p}
                                        onClick={() => this.goToPage(p + page)}
                                        className={p + page === page ? "Active" : undefined}
                                        disabled={loading}
                                    >
                                        {p + page}
                                    </PageNumber>
                                );
                            } else {
                                return null;
                            }
                        })}
                    </PageNumbers>
                )}
            </>
        );
        return (
            <StyledBrowse marginTop={50} className={cn("spring--shadow", "spring--padding")}>
                <Seo title={formatMessage(messages.browse)} description={formatMessage(messages.seoDescription)} />
                <BrowseContent>
                    <DesktopFilters>
                        {this.renderFilters()}
                        <FilterBottomAdSense>
                            <AdSense slot="Browse_Filter_Bottom" height="100%" />
                        </FilterBottomAdSense>
                    </DesktopFilters>
                    <div style={{ minWidth: 0 }}>
                        <AboveQueryAdSense>
                            <AdSense height={90} width="100%" slot="Browse_Top" />
                        </AboveQueryAdSense>
                        <QueryBar>
                            <FilterButton
                                className={"button"}
                                onClick={() => this.setState({ isFiltersModalOpen: true })}
                            >
                                <FormattedMessage {...messages.filters} />
                            </FilterButton>
                            <InputText
                                lightWithValue
                                placeholder={formatMessage(messages.searchTitleLabel)}
                                name="search"
                                value={search}
                                onChange={this.changeSearch}
                            />
                            <SortForDesktop>
                                <SortContainer>
                                    <SortLabel>
                                        <FormattedMessage {...messages.sortType} />:{" "}
                                    </SortLabel>
                                    <SortWrapper>
                                        <Select
                                            name="sortType"
                                            noDelete
                                            value={sorting}
                                            onChange={this.handleChangeSort}
                                            options={[
                                                { label: formatMessage(messages.popularity), value: "total" },
                                                { label: formatMessage(messages.newsFirst), value: "time" },
                                            ]}
                                        />
                                    </SortWrapper>
                                </SortContainer>
                            </SortForDesktop>

                            {isFiltersModalOpen && (
                                <Modal close={() => this.setState({ isFiltersModalOpen: false })}>
                                    {this.renderFilters()}
                                </Modal>
                            )}
                        </QueryBar>
                        <TopMobileAdSense>
                            <AdSense height={100} width={320} slot="Browser_Top-Mobile" />
                        </TopMobileAdSense>
                        {!!totalDocs && totalDocs > 0 && (
                            <TotalDocs>
                                <span>
                                    <FormattedMessage {...messages.totalDocs} values={{ totalDocs, timeTaken }} />
                                </span>
                                <Pagination />
                            </TotalDocs>
                        )}
                        <Hr />
                        {!loading && plans.length === 0 && (
                            <NoResult>
                                <FormattedMessage {...messages.noResults} />
                            </NoResult>
                        )}
                        <Holder>
                            {!loading && plans.length > 0 &&
                                [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(num => {
                                    const plan = getPlan(plans, adsPlace, num);
                                    if (!plan) return null;
                                    if (plan === "ad1" || plan === "ad2") {
                                        return (
                                            <AdWrapper key={`AD_${num}`}>
                                                <AdSense slot={`Browse_Card`} width="100%" height="100%" />
                                            </AdWrapper>
                                        );
                                    }
                                    return <PlanCard browse plan={plan} key={plan._id} onComposerClick={this.handleComposerClick} />;
                                })}
                            {loading &&
                                [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(plan => {
                                    return (
                                        <PlanCard
                                            browseLoading
                                            plan={{
                                                playable: true,
                                            } as unknown as IPlan}
                                            key={plan}
                                        />
                                    );
                                })}
                        </Holder>
                        <Pagination down />
                    </div>
                </BrowseContent>
                <BottomAdSense>
                    <AdSense slot="Browse_Bottom" height={120} />
                </BottomAdSense>
            </StyledBrowse>
        );
    }
}

export default Browse;
